import React, { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Wraps a route that requires `role === "admin"`. Unauthenticated users get
 * the standard login redirect. Authenticated non-admin users are bounced to
 * `/` with a toast.
 *
 * `toastRef` guards against StrictMode double-mount toasting twice.
 */
export const AdminRoute = ({ children }) => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const location = useLocation();
    const toastedRef = useRef(false);

    const isAdmin = user?.role === "admin";

    useEffect(() => {
        if (!isLoading && isAuthenticated && !isAdmin && !toastedRef.current) {
            toastedRef.current = true;
            toast.error("Admin access only");
        }
    }, [isLoading, isAuthenticated, isAdmin]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
                Loading…
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!isAdmin) {
        return <Navigate to="/" replace />;
    }

    return children;
};
